import React, { useState, useEffect } from "react";
import { ShieldCheck, BookOpen, Sparkles } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import { getErrorMessage } from "../../types";

export interface AddLearningRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: { slug: string; name: string } | null;
  workspaceCourses: Array<{ id: number; title: string }>;
  onSuccess: () => Promise<void>;
  onNavigateToCourses?: () => void;
}

export const AddLearningRuleModal: React.FC<AddLearningRuleModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  workspaceCourses,
  onSuccess,
  onNavigateToCourses,
}) => {
  const [courseId, setCourseId] = useState<number | "">("");
  const [isMandatory, setIsMandatory] = useState(true);
  const [passingThresholdScore, setPassingThresholdScore] = useState(75);
  const [dueDaysAfterEnrollment, setDueDaysAfterEnrollment] = useState<number | "">(14);
  const [remediationRequired, setRemediationRequired] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && workspaceCourses.length > 0 && !courseId) {
      setCourseId(workspaceCourses[0].id);
    }
  }, [isOpen, workspaceCourses, courseId]);

  const resetForm = () => {
    setCourseId(workspaceCourses.length > 0 ? workspaceCourses[0].id : "");
    setIsMandatory(true);
    setPassingThresholdScore(75);
    setDueDaysAfterEnrollment(14);
    setRemediationRequired(true);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !workspace || !courseId) return;

    try {
      setSaving(true);
      setError(null);
      const { data, error: apiError } = await api.createOrUpdateWorkspaceLearningRuleApiV1B2bSlugWorkspacesWsSlugRulesPost({
        path: { slug, ws_slug: workspace.slug },
        body: {
          course_id: Number(courseId),
          is_mandatory: isMandatory,
          passing_threshold_score: Number(passingThresholdScore),
          due_days_after_enrollment: dueDaysAfterEnrollment ? Number(dueDaysAfterEnrollment) : null,
          remediation_required: remediationRequired,
        },
      });

      if (data) {
        handleClose();
        await onSuccess();
      } else {
        setError(getErrorMessage(apiError, "Failed to configure rule"));
      }
    } catch (err) {
      console.error("Error saving learning rule:", err);
      setError("Network error saving learning rule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Configure Learning Rule"
      subtitle={workspace ? `For workspace: ${workspace.name}` : undefined}
      icon={<ShieldCheck className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {error}
        </div>
      )}

      {workspaceCourses.length === 0 ? (
        <div className="py-6 text-center space-y-3">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="text-xs text-slate-600">
            No courses are currently assigned to this workspace. Please link a course first in the <strong>Courses</strong> tab before configuring compliance rules.
          </p>
          {onNavigateToCourses && (
            <button
              type="button"
              onClick={() => {
                handleClose();
                onNavigateToCourses();
              }}
              className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl"
            >
              Go to Courses Tab
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Target Course *</label>
            <select
              value={courseId}
              onChange={(e) => setCourseId(Number(e.target.value))}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
            >
              {workspaceCourses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <div className="font-bold text-slate-800">Mandatory Course</div>
              <div className="text-[10px] text-slate-500">Must be completed by all workspace members</div>
            </div>
            <input
              type="checkbox"
              checked={isMandatory}
              onChange={(e) => setIsMandatory(e.target.checked)}
              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              Minimum Passing Score Threshold (%): {passingThresholdScore}%
            </label>
            <input
              type="range"
              min={50}
              max={100}
              step={5}
              value={passingThresholdScore}
              onChange={(e) => setPassingThresholdScore(Number(e.target.value))}
              className="w-full accent-indigo-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>50% (Basic)</span>
              <span>75% (Standard)</span>
              <span>100% (Mastery)</span>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">
              SLA Completion Deadline (Days after enrollment)
            </label>
            <input
              type="number"
              min={1}
              max={365}
              placeholder="e.g. 14 (Leave empty for self-paced)"
              value={dueDaysAfterEnrollment}
              onChange={(e) => setDueDaysAfterEnrollment(e.target.value ? Number(e.target.value) : "")}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <div className="font-bold text-slate-800 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                AI Remediation
              </div>
              <div className="text-[10px] text-slate-500">Auto-generate review modules if score falls below threshold</div>
            </div>
            <input
              type="checkbox"
              checked={remediationRequired}
              onChange={(e) => setRemediationRequired(e.target.checked)}
              className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500"
            />
          </div>

          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !courseId}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Rule"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};
